import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
// Redux
import { useDispatch, useSelector } from 'react-redux';
import {
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	TextField,
	Typography
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { holdTemplateNames } from '../../../../Store/Redux/file-list-slice.js';
// API
import {
	updateTemplate,
	templateCopy
} from '../../../../Services/api-config.js';
// MUI
import PrimaryButton from '../../../Common/Button/primary-button.js';
import Loading from '../../../Common/LoaderComponent/loader.js';
import { getErrorMessage } from '../../../../utils/util.js';

const CKTemplatePopup = ({ open, onClose, type, templateId }) => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const [localTemplateName, setLocalTemplateName] = useState('');
	const [initialTemplateName, setInitialTemplateName] = useState('');
	const [cancelClickedOnce, setCancelClickedOnce] = useState(false);
	const [errorMessage, setErrorMessage] = useState('');
	const [responseReceived, setResponseReceived] = useState(false);
	const { selectedFileStatus, holdTemplateName } = useSelector(
		(state) => state.fileList
	);
	const [loading, setLoading] = useState(false);

	useEffect(() => {
		if (responseReceived) {
			setResponseReceived(false);
		}

		if (type !== 'create' && holdTemplateName !== null) {
			setInitialTemplateName(holdTemplateName);
			setLocalTemplateName(holdTemplateName);
		} else {
			setLocalTemplateName('');
		}
	}, [type, holdTemplateName, responseReceived]);

	const handleUpdateAPI = async (fileName) => {
		const isNameTooLong = fileName.length > 100;

		if (isNameTooLong) {
			setErrorMessage(
				'Please provide a name less than or equal to 100 characters.'
			);
			setCancelClickedOnce(false);
			return false;
		}

		setErrorMessage('');
		return true;
	};

	const handleTemplateAction = async () => {
		setLoading(true);
		try {
			const isValid = await handleUpdateAPI(localTemplateName);

			if (!isValid) {
				return;
			}

			if (type === 'create') {
				const Response = await templateCopy(templateId, localTemplateName);

				if (Response.status === 200) {
					setResponseReceived(true);
					onClose();
					navigate(`/edit?createmode=true&fileId=${Response.data.templateId}`); // Testing
				} else {
					setErrorMessage('An error occurred while processing the API request.');
				}
			} else if (type === 'edit') {
				if (holdTemplateName === localTemplateName) {
					setErrorMessage('Name present, please try with a different name');
				} else if (selectedFileStatus === null) {
					dispatch(holdTemplateNames(localTemplateName));
					onClose(false);
				} else {
					const data = {
						templateId: selectedFileStatus.templateId,
						templateName: localTemplateName
					};
					const Response = await updateTemplate(data.templateId, data);

					if (Response.status === 200) {
						setResponseReceived(true);
						dispatch(holdTemplateNames(localTemplateName));
						onClose(false);
					} else {
						setErrorMessage(getErrorMessage(Response));
					}
				}
			}
		} catch (error) {
			setErrorMessage(error.message);
		} finally {
			setLoading(false);
		}
	};

	const handleCancelTemplate = () => {
		if (cancelClickedOnce) {
			onClose(false);
			setCancelClickedOnce(false);
			setErrorMessage('');
			setLocalTemplateName(initialTemplateName);
			setResponseReceived(false);
		} else {
			setCancelClickedOnce(true);
			setErrorMessage(
				'Are you sure you want to cancel? Click again to navigate back.'
			);
		}
	};

	const handleCloseTemplate = () => {
		onClose(false);
		setCancelClickedOnce(false);
		setErrorMessage('');
		setLocalTemplateName('');
		setResponseReceived(false);
	};

	const cancelButtonText = cancelClickedOnce ? 'ReCancel' : 'Cancel';

	return (
		<>
			<Dialog
				open={open}
				slotProps={{
					paper: {
						style: {
							width: '312px',
							borderRadius: '8px'
						}
					}
				}}
			>
				{responseReceived ? null : (
					<DialogTitle>
						{type === 'create'
							? 'Enter Copy Template Name'
							: 'Edit Template Name'}
					</DialogTitle>
				)}
				<DialogContent>
					{responseReceived ? (
						<Typography color='success'>
							{type === 'create'
								? `Template data created copy name with ${localTemplateName}. Close the view to see the new template.`
								: `Template name changed from ${selectedFileStatus.templateName} to ${localTemplateName}.`}
						</Typography>
					) : (
						<>
							<TextField
								inputRef={(input) => input?.focus()}
								margin='dense'
								placeholder={
									type === 'create'
										? 'Enter Copy Template Name'
										: 'Edit Template Name'
								}
								slotProps={{ input: { style: { borderRadius: '8px' } } }}
								fullWidth
								value={localTemplateName}
								onChange={(e) => {
									const value = e.target.value;

									// Step 1: Remove all non-alphanumeric characters and spaces
									let cleaned = value.replaceAll(/[^a-zA-Z\d ]/g, '');

									// Step 2: Allow only one space total
									const spaceCount = (cleaned.match(/ /g) || []).length;
									if (spaceCount > 1) {
										const firstSpaceIndex = cleaned.indexOf(' ');
										const before = cleaned.slice(0, firstSpaceIndex + 1);
										const after = cleaned.slice(firstSpaceIndex + 1).replaceAll(' ', '');
										cleaned = before + after;
									}

									// Step 3: Trim leading/trailing spaces
									//     cleaned = cleaned.trim();

									// Step 4: Limit to 30 characters
									cleaned = cleaned.slice(0, 100);

									setLocalTemplateName(cleaned);
									setErrorMessage(
										''
									);
								}}
							/>
							{errorMessage && (
								<Typography color='error'>{errorMessage}</Typography>
							)}
						</>
					)}
				</DialogContent>
				<DialogActions>
					{responseReceived ? (
						<PrimaryButton
							value='Close'
							onClick={handleCloseTemplate}
							color='primary'
							backgroundColor='#B7D423'
							padding='10px 30px'
							borderRadius='8px'
						/>
					) : (
						<>
							<PrimaryButton
								value={cancelButtonText}
								onClick={handleCancelTemplate}
								textcolor='#B7D423'
								padding='10px 30px'
								backgroundColor='transparent'
								borderRadius='8px'
								border='2px solid #B7D423'
							/>
							<PrimaryButton
								value='Save'
								onClick={handleTemplateAction}
								color='primary'
								disabled={
									!localTemplateName
									|| localTemplateName === initialTemplateName
								}
								backgroundColor='#B7D423'
								padding='10px 30px'
								borderRadius='8px'
							/>
						</>
					)}
				</DialogActions>
			</Dialog>
			{loading && <Loading open={loading} onClose={() => setLoading(false)} />}
		</>
	);
};

export default CKTemplatePopup;

CKTemplatePopup.propTypes = {
	open: PropTypes.any,
	onClose: PropTypes.any,
	type: PropTypes.any
};
